// The page's config, apart from app.ts so the world map (apps/world) can use
// the same lines, systems and view.

import type { GtfsPageConfig, GtfsPageLine } from '@tol/gtfs/page';
import { BAY_LINES, BAY_SYSTEM_NAMES } from '../shared/bay.ts';
import type { BaySystem } from '../shared/bay.ts';

// The railroads are drawn hollow, like London's Overground and New York's
// LIRR; BART, Muni (Metro, the F and the cable cars) and VTA light rail solid.
const RAILROADS: ReadonlySet<BaySystem> = new Set(['caltrain', 'smart', 'ace', 'amtrak']);

const LINES: readonly GtfsPageLine[] = BAY_LINES.map((l) => {
  const railroad = RAILROADS.has(l.system);
  const line: GtfsPageLine = {
    id: l.id,
    name: l.name,
    color: l.color,
    show: l.show,
    mode: railroad ? 'overground' : 'tube',
    label: l.label,
    dual: railroad,
    system: l.system,
  };
  // Hundreds of bus routes: drawn only zoomed in, thin and muted, their
  // vehicles as dots (@tol/core/minor).
  if (l.minor === true) Object.assign(line, { minor: true });
  return l.bullet === undefined ? line : Object.assign(line, { bullet: l.bullet });
});

export const BAY_PAGE: GtfsPageConfig = {
  hooks: 'bayLive',
  city: 'bay',
  lines: LINES,
  systems: BAY_SYSTEM_NAMES,
  // Downtown San Francisco; San Francisco and Oakland. Capitol Corridor runs
  // to Auburn, ACE to Stockton, SMART to Windsor, Caltrain to Gilroy.
  view: {
    bounds: {
      phone: [
        [-122.43, 37.765],
        [-122.385, 37.805],
      ],
      desktop: [
        [-122.52, 37.7],
        [-122.18, 37.88],
      ],
    },
    minZoom: 7.5,
    maxZoom: 17,
    maxBounds: [
      [-123.4, 36.7],
      [-120.5, 39.4],
    ],
  },
  credits: [
    'BART: <a href="https://www.bart.gov/schedules/developers">BART</a>',
    'Other trains, buses and track: <a href="https://511.org/open-data/transit">511.org</a>',
    'Santa Cruz buses: <a href="https://developer.scmetro.org/">Santa Cruz METRO</a> (CC BY 4.0); unofficial, no endorsement',
  ],
  // American railways run on the right.
  keepRight: true,
  timeZone: 'America/Los_Angeles',
  locale: 'en-US',
  dataName: "the operators' newest data",
  // The server fetches 511's feeds every couple of minutes (its rate limit),
  // and they're up to half a minute old when it does (BART's own, every
  // 30 s).
  staleAfterS: 240,
  alerts: true,
};
